import { useEffect, useState } from 'react';

import { Button, Dialog, Field, Input, Textarea } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { loiTheoTruong } from '../../../lib/errors';
import { useSuaLinhVuc, useTaoLinhVuc } from '../hooks/useThamDinh';
import type { LinhVuc } from '../types';

/** Mỗi dòng một miền; ô trống bị bỏ. Backend chuẩn hoá tiếp về miền trần, nên dán cả URL vẫn nhận. */
const tachDong = (v: string): string[] =>
  v.split('\n').map((d) => d.trim()).filter(Boolean);

export interface FormLinhVucProps {
  open: boolean;
  onClose: () => void;
  /** Có thì là sửa, không có thì là tạo. Chỉ cần các trường form đọc, nên nút cây thư viện dùng thẳng được. */
  dangSua?: Pick<LinhVuc, 'id' | 'ma' | 'ten' | 'mo_ta' | 'nguon_uu_tien'>;
}

export function FormLinhVuc({ open, onClose, dangSua }: FormLinhVucProps) {
  const { t } = useT();
  const dichLoi = useLoi();
  const tao = useTaoLinhVuc();
  const sua = useSuaLinhVuc();
  const chay = dangSua ? sua : tao;

  const [ma, setMa] = useState('');
  const [ten, setTen] = useState('');
  const [moTa, setMoTa] = useState('');
  const [nguon, setNguon] = useState('');

  // Nạp lại mỗi lần mở để hộp không giữ giá trị của lần mở trước.
  useEffect(() => {
    if (!open) return;
    setMa(dangSua?.ma ?? '');
    setTen(dangSua?.ten ?? '');
    setMoTa(dangSua?.mo_ta ?? '');
    setNguon((dangSua?.nguon_uu_tien ?? []).join('\n'));
    chay.reset();
    // `chay` đổi mỗi lần render nên không đưa vào deps; chỉ cần chạy khi mở hộp hoặc đổi bản ghi.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dangSua]);

  const loiTruong = loiTheoTruong(chay.error);
  const loiChung = chay.error && Object.keys(loiTruong).length === 0 ? dichLoi(chay.error) : undefined;

  function luu() {
    if (dangSua) {
      sua.mutate(
        { id: dangSua.id, du: { ten, mo_ta: moTa, nguon_uu_tien: tachDong(nguon) } },
        { onSuccess: onClose },
      );
    } else {
      tao.mutate({ ma, ten, mo_ta: moTa, nguon_uu_tien: tachDong(nguon) }, { onSuccess: onClose });
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t(dangSua ? 'thamDinh.linhVuc.formSuaTieuDe' : 'thamDinh.linhVuc.formTaoTieuDe')}
    >
      <div className="space-y-4 pt-2">
        {loiChung && (
          <p className="rounded border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">{loiChung}</p>
        )}

        {/* Mã không đổi được sau khi tạo: backend từ chối, nên lúc sửa thì ô chỉ để đọc. */}
        <Field
          label={t('thamDinh.linhVuc.nhanMa')}
          required={!dangSua}
          hint={dangSua ? undefined : t('thamDinh.linhVuc.moMa')}
          error={loiTruong['/ma']}
        >
          {(p) => (
            <Input {...p} value={ma} disabled={Boolean(dangSua)} onChange={(e) => setMa(e.target.value)} />
          )}
        </Field>

        <Field label={t('thamDinh.linhVuc.nhanTen')} required error={loiTruong['/ten']}>
          {(p) => <Input {...p} value={ten} onChange={(e) => setTen(e.target.value)} />}
        </Field>

        <Field label={t('thamDinh.linhVuc.nhanMoTa')} error={loiTruong['/mo_ta']}>
          {(p) => <Textarea {...p} rows={2} value={moTa} onChange={(e) => setMoTa(e.target.value)} />}
        </Field>

        <Field
          label={t('thamDinh.linhVuc.nhanNguon')}
          hint={t('thamDinh.linhVuc.moNguon')}
          error={loiTruong['/nguon_uu_tien']}
        >
          {(p) => <Textarea {...p} rows={4} value={nguon} onChange={(e) => setNguon(e.target.value)} />}
        </Field>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" size="md" onClick={onClose}>
            {t('chung.huy')}
          </Button>
          <Button variant="primary" size="md" loading={chay.isPending} disabled={!ten || (!dangSua && !ma)} onClick={luu}>
            {t('chung.luu')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
